import React, { useCallback, useEffect, useState } from 'react';
import { Card, IndexTable, Badge, Select, TextField, InlineStack, Button, Text, EmptySearchResult } from '@shopify/polaris';
import { statusTone, fmtAgo, boxesQuery } from './overstockHelpers';

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Printed', value: 'printed' },
  { label: 'Assigned', value: 'assigned' },
  { label: 'Shelved', value: 'shelved' },
  { label: 'Retired', value: 'retired' },
];

export default function BoxesTab({ api, warehouse, locations, onOpenBox, toast, refreshKey = 0 }) {
  const [status, setStatus] = useState('shelved');
  const [locationId, setLocationId] = useState('');
  const [q, setQ] = useState('');
  const [qText, setQText] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (qOverride) => {
    setLoading(true);
    try {
      const effectiveQ = qOverride !== undefined ? qOverride : q;
      const qs = boxesQuery({ status, locationId, q: effectiveQ });
      const j = await api(`/api/rfid/boxes${qs ? `?${qs}` : ''}`);
      setRows(j.boxes || []);
    } catch (e) { toast('err', e.message); }
    finally { setLoading(false); }
  }, [api, status, locationId, q, toast]);

  // Applied filters (status/location) refetch immediately. The SKU/product
  // text filter is applied only on Enter or Refresh (see applyQ) — it does
  // NOT belong in this effect's deps beyond `q` (the already-applied value),
  // so typing in the box never triggers a fetch per keystroke.
  useEffect(() => { load(); }, [load, refreshKey]);

  const applyQ = useCallback(() => {
    setQ(qText);
    load(qText);
  }, [qText, load]);

  const clearQ = useCallback(() => {
    setQText('');
    setQ('');
    load('');
  }, [load]);

  const locationOptions = [{ label: 'All locations', value: '' }]
    .concat((locations || []).filter(l => l.active).map(l => ({ label: l.code, value: String(l.id) })));

  return (
    <Card>
      <InlineStack gap="300" blockAlign="end" wrap>
        <Select label="Status" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
        <Select label="Location" options={locationOptions} value={locationId} onChange={setLocationId} />
        <form onSubmit={(e) => { e.preventDefault(); applyQ(); }}>
          <TextField label="SKU / product" value={qText} onChange={setQText} autoComplete="off" clearButton onClearButtonClick={clearQ} />
        </form>
        <Button onClick={applyQ} loading={loading}>Refresh</Button>
        <Text as="span" tone="subdued">{rows.length}{rows.length === 200 ? '+' : ''} boxes at {warehouse?.name}</Text>
      </InlineStack>
      <div style={{ height: 12 }} />
      <IndexTable
        resourceName={{ singular: 'box', plural: 'boxes' }}
        itemCount={rows.length}
        selectable={false}
        loading={loading}
        emptyState={<EmptySearchResult title="No boxes" description="Nothing matches these filters." withIllustration />}
        headings={[
          { title: 'Box' }, { title: 'SKU' }, { title: 'Product' }, { title: 'Qty' },
          { title: 'Location' }, { title: 'Status' }, { title: 'Last seen' }, { title: 'Assigned by' },
        ]}
      >
        {rows.map((b, i) => (
          <IndexTable.Row id={String(b.id)} key={b.id} position={i} onClick={() => onOpenBox(b.id)}>
            <IndexTable.Cell>#{b.id}</IndexTable.Cell>
            <IndexTable.Cell><span className="overstock-mono">{b.sku}</span></IndexTable.Cell>
            <IndexTable.Cell>{b.title}</IndexTable.Cell>
            <IndexTable.Cell>{b.qty_units ?? '—'}</IndexTable.Cell>
            <IndexTable.Cell>{b.location_code || '—'}</IndexTable.Cell>
            <IndexTable.Cell><Badge tone={statusTone(b.status)}>{b.status}</Badge></IndexTable.Cell>
            <IndexTable.Cell>{fmtAgo(b.last_seen_at)}</IndexTable.Cell>
            <IndexTable.Cell>{b.assigned_by || '—'}</IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>
    </Card>
  );
}
